import { useEffect, useRef } from 'react';
import { loadStripe, StripeEmbeddedCheckout } from '@stripe/stripe-js';

/**
 * Stripe's payment form, mounted inside a Nexus page (embedded Checkout).
 * Card data goes straight from Stripe's iframe to Stripe; Nexus never sees it.
 * `onComplete` only tells the page to ask Nexus: activation still waits for
 * Stripe's own confirmation, server side.
 */
export function EmbeddedPayment({
  publishableKey,
  clientSecret,
  onComplete,
  onError,
}: {
  publishableKey: string;
  clientSecret: string;
  onComplete: () => void;
  onError: () => void;
}) {
  const host = useRef<HTMLDivElement>(null);
  const done = useRef(onComplete);
  const failed = useRef(onError);
  done.current = onComplete;
  failed.current = onError;

  useEffect(() => {
    let checkout: StripeEmbeddedCheckout | null = null;
    let cancelled = false;
    (async () => {
      try {
        const stripe = await loadStripe(publishableKey);
        if (!stripe || cancelled) return;
        const instance = await stripe.createEmbeddedCheckoutPage({
          clientSecret,
          onComplete: () => done.current(),
        });
        if (cancelled) {
          instance.destroy();
          return;
        }
        checkout = instance;
        if (host.current) instance.mount(host.current);
      } catch {
        if (!cancelled) failed.current();
      }
    })();
    return () => {
      cancelled = true;
      checkout?.destroy();
    };
  }, [publishableKey, clientSecret]);

  return <div ref={host} style={{ minHeight: 420, borderRadius: 'var(--radius-md)', overflow: 'hidden' }} />;
}
